// @flow
import { isEmpty, mapValues } from 'lodash';

import {
  resolversLibrary as cmsToolkit,
  type ResolversLibrary,
} from 'Client/cms/toolkit/resolvers';
import { isSeller } from 'Utils/seller/sellerUtils';
import { invariant } from 'Utils/invariant/invariant';
import { isImmersive } from './lineUtils';

type LinePropsFromCms = { [string]: *, lines?: LinePropsFromCms[] };
type Line = {
  [string]: LinePropsFromCms,
};

type PickResolvers = (params: { list: string[], library: ResolversLibrary }) => Line[];
const pickResolvers: PickResolvers = ({ list, library }) =>
  list.map(name => (library[name] ? { [name]: library[name] } : undefined)).filter(Boolean);

type Params = {
  lines: Line[],
  appContext: AppContext,
  linesToPrepend?: string[],
  linesToAppend?: string[],
  resolversLibrary?: ResolversLibrary,
  pageContext: PageContext,
  location: PageWithLinesRouterLocation,
};

export async function formatLinesForLineComponent({
  lines,
  appContext,
  pageContext,
  location,
  linesToPrepend = [],
  linesToAppend = [],
  // resolversLibrary is provided for test purposes
  resolversLibrary = cmsToolkit,
}: Params) {
  lines.unshift(...pickResolvers({ list: linesToPrepend, library: resolversLibrary }));
  lines.push(...pickResolvers({ list: linesToAppend, library: resolversLibrary }));

  /* eslint-disable no-use-before-define */
  return Promise.all(
    lines
      .map((line, index) =>
        mapLineProps({
          location,
          line,
          resolversLibrary,
          index,
          appContext,
          pageContext,
        }),
      )
      .filter(Boolean), // filter Promises
  ).then(results => results.filter(Boolean)); // filter Promises results
  /* eslint-enable no-use-before-define */
}

type MapLinePropsAndIdParams = {
  line: Line,
  resolversLibrary: ResolversLibrary,
  baseIndex?: number,
  index: number,
  appContext: AppContext,
  pageContext: PageContext,
  location: PageWithLinesRouterLocation,
};
function mapLineProps({
  line,
  resolversLibrary,
  index,
  baseIndex = 0,
  appContext,
  pageContext,
  location,
}: MapLinePropsAndIdParams) {
  /**
   * NOTE: a line is a one-item object of shape { [name]: { name, resolve, props, etc. } }
   * For instance: { 'resort-grid': { name: 'resort-grid', resolve() {}, props(), ...etc } }
   * That's why we consider only the first object entry
   */
  const lineEntries = Object.entries(line);
  invariant(
    lineEntries.length === 1,
    // This is the kind of things that can happen when due to some wrong CMS configuration, we get
    // the @metadata, which we don't want (but are needed for migrations for instance)
    // $FlowFixMe
    `A line should contain only one property. See ${lineEntries}`,
  );
  // $FlowFixMe
  const [lineName, propsFromCms] = (lineEntries[0]: [string, LinePropsFromCms]);
  if (isSeller(appContext) && isImmersive(lineName)) return undefined;

  const id = index + baseIndex;
  // COMPUTE result.props
  const resolver = resolversLibrary[lineName] || {};
  const subLinesPromises = isEmpty(propsFromCms.lines)
    ? undefined
    : propsFromCms.lines
        .map((subline, subindex) =>
          mapLineProps({
            location,
            line: subline,
            resolversLibrary,
            index: subindex,
            baseIndex: index * 10, // FIXME: random arbitrary value that I won't change because backward compatibility.
            appContext,
            pageContext,
          }),
        )
        .filter(Boolean); // filter Promises

  return Promise.all([
    resolver.resolve &&
      resolver.resolve(appContext, propsFromCms, pageContext, location).catch(() => false),
    subLinesPromises &&
      Promise.all(subLinesPromises)
        .then(results => results.filter(Boolean)) // filter Promises results
        .catch(() => []),
  ])
    .then(([propsFromResolve = {}, lines = []]) => {
      const resolverProps = resolver.props && resolver.props(appContext, pageContext, location);
      const baseProps = {
        ...propsFromResolve,
        ...propsFromCms,
        lines,
      };
      const propsFromResolver = mapValues(
        resolverProps,
        prop => (typeof prop === 'function' ? prop(baseProps) : prop),
      );
      // END COMPUTE result.props
      return {
        id,
        props: { ...baseProps, ...propsFromResolver },
        name: lineName,
      };
    })
    .catch(() => false);
}
